import { useState, useCallback } from 'react';
import { Client, PaginatedResponse } from '../types/entities';
import { ClientFormData } from '../types/forms';
import { ApiError } from '../types/api';
import { getAuthenticatedFetch } from '../utils/api';
import { useErrorHandler } from '../utils/errorHandler';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export const useClientsApi = () => {
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const { handleApiError } = useErrorHandler();

  const fetchClients = useCallback(async (params?: { page?: number; limit?: number; appointments?: boolean; search?: string }): Promise<PaginatedResponse<Client>> => {
    try {
      setLoading(true);
      setError(null);
      
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.append('page', params.page.toString());
      if (params?.limit) searchParams.append('limit', params.limit.toString());
      if (params?.appointments !== undefined) searchParams.append('appointments', params.appointments.toString());
      if (params?.search) searchParams.append('search', params.search);
      
      const queryString = searchParams.toString();
      const url = queryString ? `/clients?${queryString}` : `/clients`;
      const authenticatedFetch = getAuthenticatedFetch();
      
      const response = await authenticatedFetch(url, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: PaginatedResponse<Client> = await response.json();
      return data;
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError);
      console.error('Failed to fetch clients:', err);
      throw apiError;
    } finally {
      setLoading(false);
    }
  }, [handleApiError]);

  const createClient = useCallback(async (formData: ClientFormData): Promise<Client> => {
    try {
      setIsSubmitting(true);
      setError(null);

      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create client: ${errorText}`);
      }

      const data: Client = await response.json();
      return data;
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError);
      console.error('Failed to create client:', err);
      throw apiError;
    } finally {
      setIsSubmitting(false);
    }
  }, [handleApiError]);

  const updateClient = useCallback(async (id: number, formData: Partial<ClientFormData>): Promise<Client> => {
    try {
      setIsSubmitting(true);
      setError(null);

      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/clients/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update client: ${errorText}`);
      }

      const data: Client = await response.json();
      return data;
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError);
      console.error('Failed to update client:', err);
      throw apiError;
    } finally {
      setIsSubmitting(false);
    }
  }, [handleApiError]);

  const deleteClient = useCallback(async (id: number): Promise<void> => {
    try {
      setIsSubmitting(true);
      setError(null);

      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/clients/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete client: ${errorText}`);
      }
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError);
      console.error('Failed to delete client:', err);
      throw apiError;
    } finally {
      setIsSubmitting(false);
    }
  }, [handleApiError]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    isSubmitting,
    error,
    fetchClients,
    createClient,
    updateClient,
    deleteClient,
    clearError,
  };
};
